import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';

import useConsults from 'services/general/consultsList';
import { useTinyAccountForHome } from 'hooks/useTinyAccountForHome';
import { useShouldShowConsults } from 'hooks/useShouldShowConsultLink';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import { getImpersonateId } from 'store/user';
import useFeature from 'services/features/useFeature';
import { Kit, KitStatuses } from '@repo/types';
import { kitResultsViewable, kitStatusParser, parseDate } from '@repo/utils';
import { Icon, Typography, TextColor } from '@repo/ui';
import getKitLabel from '@repo/utils/getKitLabel';

import { TextLink } from 'components';
import { HomeResultsStatus } from '../HomeNextSteps/HomeNextSteps';
import { FadedCard } from '../FadedCard/FadedCard';

import styles from './KitsSection.module.scss';
import KitSectionCardVersion from './KitSectionCardVersion';

const KitSectionCard = ({ kit }: { kit: Kit }) => {
  const { data: consultsList } = useConsults();
  const impersonateId = useSelector(getImpersonateId);
  const { config: portalConfig } = usePortalConfigs(kit);
  const { isFeatureEnabled: showGroupCalls } = useFeature(
    'consult_group_calls',
  );
  const hideSurveyCTAs = portalConfig.survey_type === 'skip';
  const { isFeatureEnabled: isPartnerRecommendationNoteEnabled } = useFeature("partner_recommendation_note")

  const isVoid = kitStatusParser(kit.status) === 'Void';
  const isPractitionerReview =
    kit.status === KitStatuses.RESULTS_READY_PRACTITIONER;
  const resultsViable =
    kitResultsViewable(kit, !!impersonateId) && !!kit.date_survey_completed;
  const kitType = getKitLabel(kit, true);
  const formattedDate = format(
    parseDate(kit.sample_date) ?? new Date(),
    'MM/dd/yy',
  );

  const consultWithNote = useMemo(
    () =>
      consultsList?.find(
        consult =>
          consult.status.includes('completed') &&
          consult.kit_id === kit.id &&
          consult.consult_note?.status === 'published',
      ),
    [consultsList, kit],
  );
  const shouldShowConsultLink = useShouldShowConsults(kit);
  const shouldShowPartnerRecsLink = !shouldShowConsultLink && !consultWithNote?.kit_id && !!kit.partner?.partner_notes?.content && isPartnerRecommendationNoteEnabled;

  return (
    <div className={styles.card}>
      <div className='flex align-items-center gap-2 pb-1'>
        <Typography variant='heading-m'>{formattedDate}</Typography>
        <Typography variant='heading-m'>{kitType}</Typography>
      </div>
      <div className='flex align-items-center justify-content-between gap-1 flex-wrap'>
        <div className={styles.iconRow}>
          <Icon name='priceTagFilled' size='s' />
          <Typography variant='body-s'>{`Kit ID: ${kit.id}`}</Typography>
          <KitSectionCardVersion kitId={kit.id} />
        </div>
        <div className={styles.iconRow}>
          <Icon name='atSign' size='s' />
          <Typography variant='body-s'>{kit.sample_label}</Typography>
        </div>
        {isVoid && (
          <div className='flex align-items-center gap-1 red'>
            <Typography variant='body-s'>Void</Typography>
            <Icon name='add' size='s' className={styles.rotate} />
          </div>
        )}
      </div>
      {!isVoid && isPractitionerReview && (
        <div className={styles.practitionerReviewRow}>
          <Typography variant='heading-s' color={TextColor.PURPLE}>
            Practitioner review in progress
          </Typography>
          <Typography variant='body-xs'>
            Your practitioner is reviewing your results and will release them to
            you soon.
          </Typography>
        </div>
      )}
      {!isVoid && (
        <>
          <div className='flex align-items-center justify-content-between gap-2 flex-wrap'>
            {resultsViable ? (
              <TextLink
                label='See Results'
                to={`/results/${kit.id}`}
                size='body-s'
              />
            ) : !!kit.date_results_ready ? (
              <HomeResultsStatus text='Ready' />
            ) : (
              (!!kit.date_in_transit_to_lab || !!kit.date_delivered_to_lab) && (
                <HomeResultsStatus text='Lab analysis' />
              )
            )}
            {!!consultWithNote?.kit_id && (
              <div className='hide-on-mobile'>
                <TextLink
                  label='Specialist Notes'
                  to={`/consults/${consultWithNote.kit_id}`}
                  size='body-s'
                />
              </div>
            )}
            {shouldShowConsultLink && !consultWithNote?.kit_id && (
              <div className='hide-on-mobile'>
                <TextLink
                  label={showGroupCalls ? 'Schedule a review' : 'Book a call'}
                  to={
                    showGroupCalls
                      ? `/results/${kit.id}/consults/select-method`
                      : `/results/${kit.id}/consults/book`
                  }
                  size='body-s'
                />
              </div>
            )}
            {shouldShowPartnerRecsLink && (
              <div className='hide-on-mobile'>
                <TextLink
                  label={"Partner Recs"}
                  to={`/partner-recs/${kit.partner_id}`}
                  size='body-s'
                />
              </div>
            )}
            {!hideSurveyCTAs &&
              (!kit.date_survey_completed ? (
                <TextLink
                  label='Complete info'
                  to={`/information/${kit.id}`}
                  state={{ from: `/` }}
                  size='body-s'
                />
              ) : (
                <TextLink
                  label='Health responses'
                  to={`/results/${kit.id}/surveys`}
                  state={{ from: `/` }}
                  size='body-s'
                />
              ))}
          </div>
          {!!consultWithNote?.kit_id && (
            <div className='hide-on-desktop'>
              <TextLink
                label='Specialist Note'
                to={`/consults/${consultWithNote.kit_id}`}
                size='body-s'
              />
            </div>
          )}
          {shouldShowConsultLink && !consultWithNote?.kit_id && (
            <div className='hide-on-desktop'>
              <TextLink
                label={showGroupCalls ? 'Schedule a review' : 'Book a call'}
                to={
                  showGroupCalls
                    ? `/results/${kit.id}/consults/select-method`
                    : `/results/${kit.id}/consults/book`
                }
                size='body-s'
              />
            </div>
          )}
          {shouldShowPartnerRecsLink && (
              <div className='hide-on-desktop'>
                <TextLink
                  label={"Partner Recs"}
                  to={`/partner-recs/${kit.partner_id}`}
                  size='body-s'
                />
              </div>
            )}
        </>
      )}
    </div>
  );
};

export const KitsSection = ({
  tinyAccountId,
}: {
  tinyAccountId: string | null;
}) => {
  const { getDisplayName, kits } = useTinyAccountForHome(tinyAccountId);

  if (!kits?.length) return <></>;
  return (
    <FadedCard>
      <div className={styles.container}>
        <div className='flex align-items-center justify-content-between gap-2'>
          <Typography variant='heading-l'>{`${getDisplayName()} Tests`}</Typography>
          <TextLink
            label='Activate kit'
            iconNameLeft='add'
            iconSize='m'
            to={`/activate`}
            state={{ from: `/` }}
            size={'label'}
            color={TextColor.GREY}
            className='gap-1'
          />
        </div>
        {kits?.map(kit => (
          <KitSectionCard key={kit.id} kit={kit} />
        ))}
      </div>
    </FadedCard>
  );
};
